import React, {useEffect, useState} from "react";
import {useUser} from "@/base/components/auth/UserProvider";
import {useRouter} from "next/router";
import {Button} from "antd";
import {TFunction, useTranslation} from "next-i18next";
import {useNotification} from "@/base/components/NotificationProvider";
import useFNSLocale from "@/base/utils/datefnslocale";
import {DataPageOutput, getDefaultPageOutput, MapPageOutput, PageOutput} from "@/base/types/Pagination";
import {SupportTicket} from "@/base/types/SupportTicket";
import PagedItems, {OnPageChange} from "@/base/components/dashboard/PagedItems";
import {ModifyURL} from "@/base/utils/fetch";
import SimpleCard from "@/base/components/dashboard/SimpleCart";
import Image from "next/image";
import Link from "next/link";
import {getCache} from "@/base/utils/cache";
import {format} from "date-fns/format";

type Props = {
	children: React.ReactNode;
	title?: string | ((t: TFunction) => string);
	className?: string;
	rootClassName?: string;
	innerClassName?: string;
}

export default function SupportLayout({children, className = '', rootClassName = '', innerClassName = '', title = undefined}: Props) {
	const {t} = useTranslation();

	const router = useRouter();

	const UserContext = useUser();
	const [api] = useNotification();

	const fnsLocale = useFNSLocale();

	const supportTicketsListState = useState<PageOutput<SupportTicket>>(getDefaultPageOutput(6));
	const loadSupportTickets: OnPageChange<SupportTicket> = async (page, perPage, setList, list) => {
		if (!UserContext.loggedIn)
		{
			const cache: SupportTicket[] = await getCache('support_tickets', []) || [];
			if (cache.length > 0)
			{
				setList(DataPageOutput(cache, t => t, supportTicketsListState[0]?.per_page || 6));
			}
			return;
		}

		return UserContext.Fetch({
			url: ModifyURL(`/support/tickets`),
			params: {page, perPage, sortBy: 'last_response_in', sortDesc: 'desc'}
		}).then(res => {
			setList(MapPageOutput(res?.data || {}, (item: any) => item as SupportTicket));
		}).catch(async err => {
			api.error({
				message: err.message || err
			});
		})
	}

	const [activeSupportTicketId, setActiveSupportTicketId] = useState<string | null>(null);
	useEffect(() => {
		if (router.query.id)
		{
			setActiveSupportTicketId(router.query.id as string);
		}
		else
		{
			setActiveSupportTicketId(null);
		}
	}, [router.query.id])

	return <div className={`gap-4 gap-y-16 md:gap-y-24 xl:container max-xl:mx-4 relative min-h-screen !max-w-[1580px] ${rootClassName}`}>
		<div className={`col-span-full py-10 text-center`}>
			<h1 className={`h1`}>
				{title && typeof title === 'function' ? title(t) : (title || t('base-support:title'))}
			</h1>
		</div>
		<div className={`grid lg:grid-cols-12 auto-rows-fr bg-main-bg shadow-c-md shadow-gray-400/10 rounded-2xl w-full ${innerClassName}`}>
			<SimpleCard
				className={`lg:col-span-4 !shadow-none relative flex flex-col max-lg:order-5`}
				bodyClassName={`space-y-4 !pt-0 flex flex-col h-full`}
				headerClassName={`grow-0 w-full`}
				title={<div className={`flex flex-wrap !w-full`}>
					<h2 className={`h4 grow`}>{t('base-support:ticket-list.title')}</h2>
					<Link href={`/support/create`} scroll={false}>
						<Button
							className={`!h-fit !shadow-none !px-4 !bg-primary-100`}
							size={`small`}
						>
							<Image src="/base/image/icon-plus.svg" alt="hero" width={12} height={12}/>
						</Button>
					</Link>
				</div>}>
				<PagedItems<SupportTicket>
					dependenciesResetPages={[UserContext.user, router.query.refresh_list]}
					dependencies={[UserContext.user, router.locale, router.query.refresh_list]}
					onChange={loadSupportTickets}
					state={supportTicketsListState}
					className={`mt-2`}
					rootClassName={`grow flex-1`}
					bodyClassName={`grid grid-cols-1 gap-2 h-auto place-content-start`}
					innerClassName={`mt-auto`}
					pageProps={{
						size: "small"
					}}
					renderItem={(ticket, k) => (
						<Link key={k} href={`/support/${ticket.id}`} scroll={false}>
							<div
								className={`bg-primary-50 space-y-2 px-6 py-4 rounded-xl cursor-pointer hover:bg-slate-10 dark:hover:!bg-primary-100 duration-150
								${activeSupportTicketId === ticket.id && `!bg-primary-200/80`}
								`}
							>
								<p className={`text-gray-400 text-sm`}>
									{format(new Date(ticket.created_at), 'd MMM yyyy', {locale: fnsLocale})}
								</p>
								<p className={`text-text-2`}>
									{ticket.title || t('base-support:ticket-list.no-title')}
								</p>
							</div>
						</Link>
					)}
				/>

				<div className={`bg-primary-50 rounded-r-2xl space-y-3 p-6`}>
					<h3 className={`h5`}>
						{t('base-support:we-in-social.title')}
					</h3>
					<hr className={`border-slate-400/10`}/>
					<p>
						{t('base-support:we-in-social.description')}
					</p>
					<div className={`flex flex-wrap gap-2`}>
						{process.env.NEXT_PUBLIC_SOCIAL_MAIL && <Link
							href={`mailto:${process.env.NEXT_PUBLIC_SOCIAL_MAIL}`}
							target={`_blank`}
							className={`hover:opacity-50 duration-100`}
						>
							<Image src="/base/image/icon-social-mail.svg" alt="mail" width={30} height={18}/>
						</Link>}
						{process.env.NEXT_PUBLIC_SOCIAL_TELEGRAM && <Link
							href={process.env.NEXT_PUBLIC_SOCIAL_TELEGRAM}
							target={`_blank`}
							className={`hover:opacity-50 duration-100`}
						>
							<Image src="/base/image/icon-social-telegram.svg" alt="mail" width={18} height={18}/>
						</Link>}
					</div>
				</div>
				<div className={`right-0 top-[5%] w-[1px] bg-gray-300/20 absolute h-[90%] `}/>
			</SimpleCard>
			<div className={`lg:col-span-8 max-lg:-order-1 ${className} relative rounded-2xl overflow-hidden`}>
				{children}
			</div>
		</div>
	</div>
}